import React, { useEffect, useState } from "react";
import { LucideIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  sectionRef: React.RefObject<HTMLElement>;
  url?: string;
  type?: string;
}

interface SidebarNavigationProps {
  menuItems: SidebarMenuItem[];
  className?: string;
  activeItem?: string;
  onItemClick?: (itemId: string) => void;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  menuItems,
  className = "",
  activeItem,
  onItemClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentActiveItem, setCurrentActiveItem] = useState<string | null>(
    () => {
      // Initialize with activeItem prop or detect from current URL
      if (activeItem) return activeItem;

      const currentPath = location.pathname + location.search;
      const matchingItem = menuItems.find((item) => {
        if (!item.url) return false;

        // Extract path and search from item URL
        const itemUrl = new URL(item.url, window.location.origin);
        const itemPath = itemUrl.pathname;
        const itemSearch = itemUrl.search;

        // Check if current path and search match the item URL
        return currentPath === itemPath + itemSearch;
      });

      return matchingItem ? matchingItem.id : null;
    }
  );

  // Update active item when location or activeItem prop changes
  useEffect(() => {
    const newActiveItem = getActiveItem();
    setCurrentActiveItem(newActiveItem);

    // Call onItemClick if there's a new active item and no explicit activeItem prop
    if (!activeItem && newActiveItem && onItemClick) {
      onItemClick(newActiveItem);
    }
  }, [location.pathname, location.search, activeItem]);

  // Handle initial mount - ensure active state is set
  useEffect(() => {
    if (!currentActiveItem) {
      const initialActiveItem = getActiveItem();
      if (initialActiveItem) {
        setCurrentActiveItem(initialActiveItem);
        if (!activeItem && onItemClick) {
          onItemClick(initialActiveItem);
        }
      }
    }
  }, []);

  const handleItemClick = (item: SidebarMenuItem) => {
    // Navigate to URL if provided
    if (item.url) {
      navigate(item.url);
    } else {
      // Smooth scroll to the target section using ref
      if (item.sectionRef.current) {
        item.sectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Call the optional click handler
    if (onItemClick) {
      onItemClick(item.id);
    }
  };

  const getActiveItem = () => {
    // First check if there's an explicit activeItem prop
    if (activeItem) {
      return activeItem;
    }

    // Then check if current location matches any menu item URL
    const currentPath = location.pathname + location.search;
    const matchingItem = menuItems.find((item) => {
      if (!item.url) return false;

      // Extract path and search from item URL
      const itemUrl = new URL(item.url, window.location.origin);
      const itemPath = itemUrl.pathname;
      const itemSearch = itemUrl.search;

      // Check if current path and search match the item URL
      return currentPath === itemPath + itemSearch;
    });

    const activeId = matchingItem ? matchingItem.id : null;

    return activeId;
  };

  return (
    <div className={`sticky-top ${className}`}>
      <div className="card sidebar">
        <ul className="nav nav-pills flex-column">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = currentActiveItem === item.id;

            return (
              <li
                key={item.id}
                className={`sidebar-menu-item pointer mb-1 ${
                  isActive ? "active" : ""
                }`}
                onClick={() => handleItemClick(item)}
              >
                <span>
                  <IconComponent className="me-2" size={20} />
                  {item.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SidebarNavigation;
